import { useState, type ReactNode } from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { parseISO } from 'date-fns/parseISO';
import { format as formatJalali } from 'date-fns-jalali/format';
import {
  redirectToPreRegistrationPaymentPage,
  resolveStudentPreRegistrationFee,
} from '@/services/dormitoryStudent.service';
import { PreRegistrationStatus } from '@/configs';
import { showError } from '@/services';
import { parseError } from '@/utils';

const DATETIME_FORMAT = 'yyyy/MM/dd HH:mm';

function formatDateTime(iso: string | null | undefined) {
  if (!iso) {
    return '—';
  }
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return formatJalali(d, DATETIME_FORMAT);
  }
  catch {
    return iso;
  }
}

function ResultRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-1 text-base leading-snug">
      <span className="shrink-0 text-stone-700">{label}:</span>
      <span className="min-w-0 font-medium text-stone-900">{children}</span>
    </div>
  );
}

export interface DormitoryPreRegistrationResultProps {
  record: StudentPreRegistrationMeEntity;
  event: DormitoryStudentPreRegistrationEvent;
}

/** Shows the student’s current v2 pre-registration status (and payment actions when due). */
export function DormitoryPreRegistrationResult({
  record,
  event,
}: DormitoryPreRegistrationResultProps) {
  const [t] = useTranslation(['common', 'dormitory']);
  const [payLoading, setPayLoading] = useState(false);

  const fee = resolveStudentPreRegistrationFee(event, record.suggestedDormitoryId);
  const showPaymentFields = fee > 0
    || record.preRegistrationFeePaid === true
    || Boolean(record.preRegistrationFeePaymentDeadline);
  const showPayButton = showPaymentFields
    && record.preRegistrationFeePaid !== true
    && record.preRegistrationStatus !== PreRegistrationStatus.Expire;

  const onPay = async () => {
    setPayLoading(true);
    try {
      await redirectToPreRegistrationPaymentPage();
    }
    catch (e) {
      showError(parseError(e, t('common:errors.get-permission')));
    }
    finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="my-2 flex w-full min-w-0 flex-col gap-4">
      <Alert
        type={record.preRegistrationStatus === PreRegistrationStatus.Expire ? 'error' : 'warning'}
        showIcon
        className="p-4 [&_.ant-alert-message]:w-full"
        message={(
          <div className="flex w-full min-w-0 flex-col gap-3 text-base leading-snug">
            <p className="m-0">{t('dormitory:errors.pre-register-already-done')}</p>
            <div className="flex flex-col gap-2">
              <ResultRow label={t('dormitory:labels.dormitory-requested')}>
                {record.suggestedDormitoryName?.trim() || '—'}
              </ResultRow>
              <ResultRow label={t('common:labels.status')}>
                {t(`dormitory:labels.pre-registration-status-${record.preRegistrationStatus}`, {
                  defaultValue: record.preRegistrationStatus,
                })}
              </ResultRow>
              {showPaymentFields && (
                <>
                  <ResultRow label={t('dormitory:labels.pre-registration-payment-status')}>
                    {record.preRegistrationFeePaid
                      ? t('dormitory:labels.pre-registration-payment-paid')
                      : t('dormitory:labels.pre-registration-payment-unpaid')}
                  </ResultRow>
                  {record.preRegistrationFeePaid && record.preRegistrationFeePaymentTime && (
                    <ResultRow label={t('dormitory:labels.pre-registration-fee-payment-time')}>
                      <span dir="ltr" className="tabular-nums">
                        {formatDateTime(record.preRegistrationFeePaymentTime)}
                      </span>
                    </ResultRow>
                  )}
                  {record.preRegistrationFeePaymentDeadline && (
                    <ResultRow label={t('dormitory:labels.pre-registration-fee-payment-deadline')}>
                      <span dir="ltr" className="tabular-nums">
                        {formatDateTime(record.preRegistrationFeePaymentDeadline)}
                      </span>
                    </ResultRow>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      />

      {showPayButton && (
        <div className="flex flex-row justify-end">
          <Button type="primary" size="large" loading={payLoading} onClick={onPay}>
            {t('dormitory:labels.pre-register-pay-fee')}
          </Button>
        </div>
      )}
    </div>
  );
}
